import React, {useEffect, useState} from "react";
import styled from "styled-components/macro";
import {
    Box, Grid, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableRow,
} from "@material-ui/core";
import {useAuth} from "../../context/auth";
import {getIndividualRubricScoreService, getNationalGlobalRubricScoreService} from "../../services/individual.service";
import LoaderWithBackDrop from "../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {LEVEL_TO_STATUS, STATUS_COLORS, STATUS_ORDER, STATUS_ORDER_INDEX_TO_COLOR_KEY} from "../../utils/constants";
import {statusToDisplay} from "./SchoolScore/hooks/useBarChartData";
import {useGlobalState} from "../../context/global_state";
import {getRubricNames} from "../../utils/utils";

const categoryBgGrey = "#faf7f6";
const headerBg = "#111";

// Styled cells
const LegendSquare = styled(Box)`
    width: 110px;
    height: 32px;
    background: ${props => props.color};
    color: ${props => (props.textColor ? props.textColor : "#fff")};
    font-weight: 700;
    font-size: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    @media (min-width: 768px) and (max-width: 1024px) {
        font-size: 9px;
        width: 125px;
    }
    @media (max-width: 768px) {
        font-size: 8px;
    }
`;

const StatusCell = styled(TableCell)`
    && {
        background: ${({filled, color, isHeader, isGreyed}) =>
                filled ? color
                        : isHeader ? headerBg
                                : isGreyed ? categoryBgGrey
                                        : "#fff"};
        color: ${({filled, isHeader}) => (filled || isHeader ? "#fff" : "#222")};
        font-weight: ${({filled, isHeader}) => (filled || isHeader ? 700 : 400)};
        font-size: 12px;
        text-align: center;
        border: 1px solid #ccc;
        padding: 20px;
        min-width: 200px;
        height: 44px;
    }
`;

const CategoryCell = styled(TableCell)`
    && {
        background: ${({header, alt}) => header ? headerBg : (alt ? categoryBgGrey : "#fff")};
        color: ${({header}) => (header ? "#fff" : "#222")};
        font-weight: 700;
        font-size: 12px;
        text-align: center;
        border: 1px solid #ccc;
        vertical-align: middle;
        min-width: ${({header}) => (header ? "150px" : "110px")};
        white-space: normal;
        padding: ${({header}) => (header ? "0 8px" : "0 4px")};
    }
`;

const LabelCell = styled(TableCell)`
    && {
        background: ${({header, alt}) => header ? headerBg : (alt ? categoryBgGrey : "#fff")};
        color: ${({header}) => (header ? "#fff" : "#222")};
        font-weight: 700;
        font-size: 12px;
        border: 1px solid #ccc;
        min-width: 200px;
        text-align: center;
        padding-left: 18px;
        padding-right: 0;
        white-space: pre-line;
    }
`;

const TablePaper = styled(Paper)`
    padding: 0;
    background: #fafbfc;
    max-width: 950px;
    min-width: 950px;
    margin-left: 24px;
    box-shadow: none;
`;

function ChildScore() {
    const { authUser } = useAuth();
    const { org_code } = authUser;
    const { org_country } = authUser.org_obj;
    const { settings } = useGlobalState();
    const [user_id, module_code] = (settings.selected_report || '').split('___');
    const [loading, setLoading] = useState(false);
    const [childScoreTableData, setChildScoreTableData] = useState([]);

    function buildScoreTableData({rubric_individual = [], rubric_national_global = [], assess_indicators = [],}) {
        const overall = assess_indicators.map((indicator, i) => {
            const individual = rubric_individual.find(r => r.assess_code === indicator.indicator_code);
            const national = rubric_national_global.find(r => r.assess_code === indicator.indicator_code && r.country !== "Global");
            const global = rubric_national_global.find(r => r.assess_code === indicator.indicator_code && r.country === "Global");
            const rubricNames = getRubricNames(indicator);

            return {
                category: indicator.name,
                code: indicator.indicator_code,
                greyed: i % 2 === 0,
                rubricNames,
                rows: [
                    {
                        label: "Your Child",
                        highlightCol: individual?.rubricLevel ? 4 - individual.rubricLevel : null, // 0=veryActive, 1=active,...
                    },
                    {
                        label: "National Average",
                        highlightCol: national?.rubricLevel ? 4 - national.rubricLevel : null,
                    },
                    {
                        label: "Global Average",
                        highlightCol: global?.rubricLevel ? 4 - global.rubricLevel : null,
                    },
                ],
            };
        });

        console.log(overall);

        return overall;
    }

    const loadPageData = async () => {
        if (!org_code || !module_code || !user_id) {
            setChildScoreTableData([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const result_individual = await getIndividualRubricScoreService(
                org_code,
                module_code,
                user_id
            );

            console.log('result_individual', result_individual);

            const { rubric_individual, assess_indicators } = result_individual.data;

            let rubric_national_global = [];
            if (rubric_individual && rubric_individual.length > 0) {
                const assess_code_list = rubric_individual.map(item => item.assess_code);
                const result_national_global = await getNationalGlobalRubricScoreService(
                    org_country,
                    assess_code_list
                );
                rubric_national_global = result_national_global.data.rubric_national_global || [];
            }

            // Build the dynamic table data
            setChildScoreTableData(buildScoreTableData({
                rubric_individual,
                rubric_national_global,
                assess_indicators,
            }));

        } catch (e) {
            console.log("Some error occurred while fetching Rubric Score", e)
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        loadPageData().then(d => console.log('Rubric Score Fetched', d));
    }, [org_code, module_code, user_id]);

    return (
        <Box px={{ xs: 1, md: 4 }} py={5} width="100%">
            <Grid container spacing={1}>
                <Grid item xs={12} md={12}>
                    <Box display="flex" alignItems="center" mb={7} pl={4} pr={2}>
                        <Box width="100%">
                            <Typography variant="h5" style={{ fontWeight: 700, textAlign:'center',  marginBottom: 20 }}>
                                Check Your Child's Score
                            </Typography>
                            <Typography variant="body1" style={{ fontSize: 17,textAlign:'left', color: "#222", marginBottom: 16 }}>
                                You can view your child's score compared to national and global averages.
                            </Typography>
                            <Typography variant="body1" style={{ fontSize: 16,textAlign:'left', color: "#222" }}>
                                For details on interpreting the scores, please refer to the "Scoring Rubric" section under "Report Overview".
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" pr={7} mb={10} mt={7} justifyContent={'center'}>
                        {STATUS_ORDER.map((key, index) => {
                            const colorKey = !key || key === ""
                                ? STATUS_ORDER_INDEX_TO_COLOR_KEY[index] 
                                : key;
                            const color = STATUS_COLORS[colorKey];
                            return (
                                <LegendSquare
                                    key={index}
                                    color={color}
                                    textColor={colorKey === "active" ? "#222" : "#fff"}
                                    style={{ margin: 0, borderRadius: 0 }}
                                >
                                    {key ? statusToDisplay(key) : ""}
                                </LegendSquare>
                            );
                        })}
                    </Box>
                </Grid>
                <Grid item xs={12} md={12} >
                        <TableContainer>
                            <Table>
                                <TableBody>
                                    {(!childScoreTableData || childScoreTableData.length === 0) ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" p={20}>
                                                No data available.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        (() => {
                                            let globalRow = 0;
                                            const rows = [];
                                            childScoreTableData.forEach((section, idx) => {
                                                section.rows.forEach((row, rIdx) => {
                                                    globalRow += 1;
                                                    const isTop3 = globalRow <= 3;
                                                    rows.push(
                                                        <TableRow key={`${section.category}-${row.label}`}>
                                                            {rIdx === 0 && (
                                                                <CategoryCell
                                                                    rowSpan={3}
                                                                    alt={section.greyed}                                                                // you can add any extra styling for isTop3 here if you want
                                                                    style={isTop3 ? { background: "#111", color: "#fff" } : undefined}
                                                                >
                                                                    {section.category}
                                                                </CategoryCell>
                                                            )}
                                                            <LabelCell alt={section.greyed}                                                                 // you can add any extra styling for isTop3 here if you want
                                                                       style={isTop3 ? { background: "#111", color: "#fff" } : undefined}
                                                            >
                                                                {row.label}
                                                            </LabelCell>
                                                            {section.rubricNames.map((rubricName, colIdx) => {
                                                                // filled: should be true only if this is the highlight col
                                                                const isFilled = row.highlightCol === colIdx;
                                                                const statusColor = STATUS_COLORS[Object.keys(STATUS_COLORS)[colIdx]];
                                                                return (
                                                                    <StatusCell
                                                                        key={rubricName}
                                                                        filled={isFilled}
                                                                        color={isFilled ? statusColor : undefined}
                                                                        isGreyed={section.greyed}
                                                                        // Only force black bg for isTop3 if not filled
                                                                        style={
                                                                            isTop3 && !isFilled
                                                                                ? { background: "#111", color: "#fff" }
                                                                                : undefined
                                                                        }
                                                                    >
                                                                        {rubricName}
                                                                    </StatusCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    );
                                                });
                                            });
                                            return rows;
                                        })()
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                </Grid>
            </Grid>
            <LoaderWithBackDrop loading={loading}/>
        </Box>
    );
}

export default ChildScore;
